import {selectDefined} from '@benchling/js-utils';
import {ReactElement} from 'react';
import _ from 'underscore';
import {v4 as uuidv4} from 'uuid';

import {EventConstants} from 'constants/EventConstants';
import AppDispatcher from 'lib/Dispatcher';
import * as ToastActions from 'nav/toasts/ToastActions';
import {ToastType} from 'nav/toasts/ToastConstants';

import * as TrayTaskConstants from './TrayTaskConstants';

export interface Action {
  actionText: string;
  onActionClick: () => void;
}

interface TrayTaskOptions {
  heading: string | ReactElement;
  subheading?: string | ReactElement;
  onHeadingClick?: () => void;
  onSubheadingClick?: () => void;
  error?: string | ReactElement;
  progress?: number;
  duration?: number;
  hidden?: boolean;
  actions?: Array<Action>;
  icon?: JSX.Element;
  estimatedTotalTimeMs?: number;
}

export type TaskProgress = number | {lengthComputable: true; loaded: number; total: number};

function addTrayTask(taskId: string, options: TrayTaskOptions): void {
  AppDispatcher.dispatch({
    actionType: EventConstants.TRAY_TASK_ADD,
    taskId,
    options,
  });
}

function getProgress(e: TaskProgress): number {
  const XHR_UPLOAD_PROPORTION = 75; // Percentage of the progress bar associated with xhr upload progress.

  if (_.isNumber(e)) {
    // Our own code may call notify() with a percentage, so we just return it as is.
    return e;
  } else if (e.lengthComputable) {
    return (XHR_UPLOAD_PROPORTION * e.loaded) / e.total;
  } else {
    return TrayTaskConstants.INDETERMINATE_PROGRESS;
  }
}

function maybeHideProgress(progress?: number): number | undefined {
  return progress === TrayTaskConstants.HIDE_PROGRESS ? undefined : progress;
}

// returns the id of the task it created
export function createTrayTask(options: TrayTaskOptions): string {
  // Use a randomly assigned id to ensure that task ids are meaningless between sessions. With incremental
  // task ids (i.e. _.uniqueId), two different items in separate sessions might share the same task id.
  const id = uuidv4();
  if (!options.hidden) {
    ToastActions.pushToast(
      selectDefined({
        id,
        type: ToastType.INFO,
        message: options.heading,
        progress: maybeHideProgress(options.progress || 0),
        isCloseable: false,
        duration: options.duration || 0,
        actions: options.actions ?? [],
      })
    );
  }
  addTrayTask(id, options);
  return id;
}

export function onProgressFor(taskId: string): (e: TaskProgress) => void {
  return (e: TaskProgress) => {
    updateTrayTask(taskId, {progress: getProgress(e)});
  };
}

export function onMultiProgressFor(
  parentTaskId: string,
  numTasksInParent: number
): (index: number, taskId: string, e: TaskProgress) => void {
  const totalCompleted = Array(numTasksInParent).fill(0);
  return (index: number, taskId: string, e: TaskProgress) => {
    totalCompleted[index] = getProgress(e);
    const progress = totalCompleted.reduce((t, s) => t + s) / numTasksInParent;
    updateTrayTask(parentTaskId, {progress});
  };
}

export function updateTrayTask(taskId: string, options: Partial<TrayTaskOptions>): void {
  const toastProps = selectDefined({
    id: taskId,
    message: options.subheading || options.heading || undefined,
    duration: options.duration || 0,
  });
  if (options.progress === TrayTaskConstants.FAILURE_PROGRESS) {
    // eslint-disable-next-line no-restricted-syntax
    _.extend(toastProps, {
      message: options.error,
      progress: null,
      isCloseable: true,
      type: ToastType.DANGER,
      actions: options.actions,
    });
  } else if (options.progress === TrayTaskConstants.SUCCESS_PROGRESS) {
    // eslint-disable-next-line no-restricted-syntax
    _.extend(toastProps, {
      actionText: options.actions ? undefined : 'Open',
      onActionClick: options.actions
        ? undefined
        : options.onHeadingClick || options.onSubheadingClick || undefined,
      progress: null,
      isCloseable: true,
      type: ToastType.INFO, // TODO(T6077): Use INFO since success toats don't support CTAs yet
      actions: options.actions,
    });
  }
  // eslint-disable-next-line benchlint/no-in-operator
  else if ('progress' in options) {
    // eslint-disable-next-line no-restricted-syntax
    _.extend(toastProps, {
      progress: maybeHideProgress(options.progress),
      isCloseable: false,
      type: ToastType.INFO,
    });
  }
  ToastActions.pushToast(toastProps);
  AppDispatcher.dispatch({
    actionType: EventConstants.TRAY_TASK_UPDATE,
    taskId,
    options,
  });
}

export function removeTrayTask(taskId: string): void {
  ToastActions.closeToast(taskId);
  AppDispatcher.dispatch({
    actionType: EventConstants.TRAY_TASK_REMOVE,
    taskId,
  });
}
